import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { InstitutionResponse, SecurityResponse } from '../../../../response/responseSchema';
import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import TabMenu from '@client/uikit/TabMenu';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import SecurityHistory, { TargetField } from './SecurityHistory';
import StockLogo from '../../common/StockLogo';

class ViewStore {
    readonly institutionSlug: string;
    readonly securityHash: string;

    security: SecurityResponse | undefined;
    institution: InstitutionResponse | undefined;

    constructor(institutionSlug: string, securityHash: string) {
        this.institutionSlug = institutionSlug;
        this.securityHash = securityHash;

        makeAutoObservable(this);
    }

    async fetch() {
        if (!this.institutionSlug || !this.securityHash) {
            return;
        }

        const [institution, security] = await Promise.all([
            apiClient.institution.public.getInstitution.$query({
                institutionSlug: this.institutionSlug,
            }),
            apiClient.security.public.getSecurity.$query({
                securityHash: this.securityHash,
            }),
        ]);

        runInAction(() => {
            this.institution = institution;
            this.security = security;
        });
    }
}

const SecurityDetailPage = observer(() => {
    const { institutionSlug, securityHash } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const DEFAULT_TAB = 'weight' as const;
    const targetField = (searchParams.get('tab') || DEFAULT_TAB) as TargetField;
    const viewStore = useMobxInstance(ViewStore, institutionSlug as string, securityHash as string);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const onSelectTab = (targetField: TargetField) => {
        setSearchParams(
            (prev) => {
                if (targetField && targetField !== DEFAULT_TAB) {
                    prev.set('tab', targetField);
                } else {
                    prev.delete('tab');
                }
                return prev;
            },
            {
                preventScrollReset: true,
                replace: true,
            },
        );
    };

    const institutionName = viewStore.institution?.nameKo || viewStore.institution?.name;
    const securityName =
        viewStore.security?.stock?.nameKo ||
        viewStore.security?.stock?.name ||
        viewStore.security?.name;

    const ticker = viewStore.security?.stock?.ticker;

    return (
        <Frame>
            <Frame.Title title={institutionName} />
            <Box>
                <div className="flex flex-row items-start gap-3">
                    {viewStore.security && (
                        <div className="mt-0.5 flex items-center">
                            <StockLogo
                                size={10}
                                stock={viewStore.security?.stock}
                                altName={securityName}
                            />
                        </div>
                    )}
                    <div className="flex w-full flex-col">
                        {ticker && (
                            <div className="text-neutral text-base leading-tight font-bold">
                                {ticker}
                            </div>
                        )}
                        <div>
                            {securityName}
                            <div className="text-base-content/70 text-sm">
                                {viewStore.security?.name}
                            </div>
                        </div>
                    </div>
                </div>
            </Box>
            <div className="flex flex-row justify-center">
                <TabMenu<TargetField>
                    className="tabs-sm"
                    selected={targetField}
                    onChange={onSelectTab}
                >
                    <TabMenu.Item className="px-4" value="weight">
                        비중
                    </TabMenu.Item>
                    <TabMenu.Item className="px-4" value="value">
                        가치
                    </TabMenu.Item>
                    <TabMenu.Item className="px-4" value="amount">
                        수량
                    </TabMenu.Item>
                    <TabMenu.Item className="px-4" value="valueRank">
                        순위
                    </TabMenu.Item>
                </TabMenu>
            </div>
            <Box>
                <SecurityHistory
                    institutionSlug={viewStore.institutionSlug}
                    securityHash={viewStore.securityHash}
                    targetField={targetField}
                />
            </Box>
        </Frame>
    );
});

export default SecurityDetailPage;
